// src/ConsumerApp/Screens/Home/components/FeaturedCategoriesSection.js

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../../../assets/theme/theme';
import ArrowRight from '../../../../assets/icons/ArrowRight.svg';
import ProductCard from '../../ProductsList/components/ProductCard';
import ProductOptionsModal from '../../ProductDetails/components/ProductOptionsModal';

// Shared API Hooks mirroring ProductsList.js architecture
import {
  useGetBucketSummary,
  useGetProductVariants,
} from '../../../api/products/product.hooks';
import {
  useGetSavedList,
  useAddItemToSavedList,
  useRemoveItemFromSavedList,
} from '../../../api/savedList/savedList.hooks';
import { useGetDefaultDeliveryAddress } from '../../../api/projects/project.hooks';

const IMAGE_BASE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

export default function FeaturedCategoriesSection({ categorySections = [] }) {
  const navigate = useNavigate();

  // 1. Session Context
  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id || customer?.id;

  // 2. Options Sheet State (matching ProductsList.js)
  const [isOptionsSheetVisible, setIsOptionsSheetVisible] = useState(false);
  const [selectedProductWithOptions, setSelectedProductWithOptions] = useState([]);
  const [isOptionsAvailableForBuyNow, setIsOptionsAvailableForBuyNow] = useState(true);

  // 3. API Queries & Mutations
  const { data: rawAddress } = useGetDefaultDeliveryAddress(customerId);
  const { data: bucketSummary } = useGetBucketSummary(customerId);
  const { data: savedList = [] } = useGetSavedList(customerId);

  const addItemToSavedMutation = useAddItemToSavedList();
  const removeItemFromSavedMutation = useRemoveItemFromSavedList();
  const { mutateAsync: fetchProductVariants } = useGetProductVariants();

  const hubId =
    rawAddress?.data?.address?.hub_id ||
    rawAddress?.data?.hub_id ||
    rawAddress?.hub_id ||
    customer?.hubId ||
    customer?.hub_id;

  // 4. O(1) Lookup Set for Saved/Shortlisted Products
  const savedItemsSet = useMemo(() => {
    const list = Array.isArray(savedList) ? savedList : savedList?.items || [];
    return new Set(
      list.map((item) => String(item.product_id || item.item_id || item._id))
    );
  }, [savedList]);

  // 5. Bucket Item Map for Live Server Quantity Sync
  const bucketItemsMap = useMemo(() => {
    const map = new Map();
    const buyNowItems = bucketSummary?.buy_now_bucket?.items || [];
    buyNowItems.forEach((bItem) => {
      if (bItem?.item_id) {
        map.set(String(bItem.item_id), {
          quantity: bItem.quantity,
          bucketId:
            bucketSummary?.buy_now_bucket?.bucket_id ||
            bucketSummary?.buy_now_bucket?._id,
        });
      }
    });
    return map;
  }, [bucketSummary]);

  // 6. Bookmark Toggle Handler matching ProductsList.js
  const handleToggleSave = async (item, shouldSave) => {
    if (!customerId) {
      navigate('/consumer');
      return false;
    }
    const pid = String(item.product_id || item._id || item.id);
    try {
      if (shouldSave) {
        await addItemToSavedMutation.mutateAsync({
          customer_id: customerId,
          product_id: pid,
        });
      } else {
        await removeItemFromSavedMutation.mutateAsync({
          customer_id: customerId,
          product_id: pid,
        });
      }
      return true;
    } catch (err) {
      console.error('Failed to toggle saved item:', err);
      return false;
    }
  };

  // 7. Multi-Variant Options Modal Trigger matching ProductsList.js
  const handleOpenOptions = async (productItem, isBuyNowAvailable) => {
    const pid = String(productItem.product_id || productItem._id);
    try {
      const purchase_mode = isBuyNowAvailable ? 'Buy Now' : 'Quote';
      const variantsResponse = await fetchProductVariants({
        product_id: pid,
        purchase_mode,
      });
      const list = Array.isArray(variantsResponse)
        ? variantsResponse
        : variantsResponse?.product_variants || [];

      setSelectedProductWithOptions(list);
      setIsOptionsAvailableForBuyNow(isBuyNowAvailable);
      setIsOptionsSheetVisible(true);
    } catch (error) {
      console.error('Error fetching product options:', error);
    }
  };

  if (!categorySections || categorySections.length === 0) return null;

  return (
    <div style={styles.wrapper}>
      {/* Scoped CSS for single-row horizontal card scroll */}
      <style>{`
        .featured-products-scroll {
          display: flex;
          overflow-x: auto;
          overflow-y: visible;
          gap: 16px;
          padding: 6px 4px 18px 4px;
          scroll-snap-type: x mandatory;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
        }

        .featured-products-scroll::-webkit-scrollbar {
          display: none;
        }

        .featured-card-wrapper {
          width: 176px;
          min-width: 176px;
          flex-shrink: 0;
          scroll-snap-align: start;
          box-sizing: border-box;
        }

        @media (max-width: 640px) {
          .featured-card-wrapper {
            width: 154px;
            min-width: 154px;
          }
        }
      `}</style>

      {categorySections.map((section) => {
        const {
          product_category_id,
          product_category_name,
          product_category_image,
          products = [],
        } = section;

        if (!products || products.length === 0) return null;

        const catImg = product_category_image?.startsWith('http')
          ? product_category_image
          : `${IMAGE_BASE_URL}${product_category_image}`;

        const handleNavigateToCategory = () => {
          navigate('/consumer/productslist', {
            state: {
              categoryId: product_category_id,
              categoryName: product_category_name,
            },
          });
        };

        return (
          <section key={product_category_id} style={styles.categorySection}>
            {/* Category Header Row */}
            <div style={styles.sectionHeaderRow}>
              <div style={styles.headerTitleGroup} onClick={handleNavigateToCategory}>
                {product_category_image && (
                  <img
                    src={catImg}
                    alt={product_category_name}
                    style={styles.categoryHeaderIcon}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <h3 style={styles.sectionTitle}>{product_category_name}</h3>
              </div>

              <div style={styles.seeAllBtn} onClick={handleNavigateToCategory}>
                <span style={styles.seeAllText}>See All</span>
                <img src={ArrowRight} alt="arrow" style={{ width: 12, height: 12 }} />
              </div>
            </div>

            {/* Horizontal Product List directly reusing ProductCard */}
            <div className="featured-products-scroll">
              {products.map((prod) => {
                const pid = String(prod.product_id || prod._id);
                const bucketEntry = bucketItemsMap.get(pid);

                return (
                  <div key={pid} className="featured-card-wrapper">
                    <ProductCard
                      item={prod}
                      customerId={customerId}
                      hubId={hubId}
                      savedBucketQuantity={bucketEntry?.quantity}
                      bucketId={bucketEntry?.bucketId}
                      isSavedItem={savedItemsSet.has(pid)}
                      onToggleSave={handleToggleSave}
                      onOptionsClick={handleOpenOptions}
                      onItemClick={() =>
                        navigate('/consumer/productdetails', {
                          state: { productId: pid },
                        })
                      }
                    />
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {/* Product Options Modal matching ProductsList.js */}
      <ProductOptionsModal
        visible={isOptionsSheetVisible}
        onClose={() => setIsOptionsSheetVisible(false)}
        cardTitle={isOptionsAvailableForBuyNow ? 'Add To Buy' : 'Add To Quote'}
        productData={selectedProductWithOptions}
        bucketItemsMap={bucketItemsMap}
        customerId={customerId}
        hubId={hubId}
        purchaseType={isOptionsAvailableForBuyNow ? 'BUY_NOW' : 'RFQ'}
      />
    </div>
  );
}

const styles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: `${theme.spacing[300]}px`,
    width: '100%',
  },
  categorySection: {
    width: '100%',
  },
  sectionHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  headerTitleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
  },
  categoryHeaderIcon: {
    width: 24,
    height: 24,
    objectFit: 'contain',
    borderRadius: 6,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#2B2A29',
    margin: 0,
    fontFamily: theme.typography.headingMD.fontFamily,
  },
  seeAllBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
  },
  seeAllText: {
    fontSize: 12.5,
    fontWeight: '600',
    color: theme.colors.brand,
  },
};